import { useFragment, graphql } from 'react-relay';
import { useIntl, defineMessages, type MessageDescriptor, type IntlShape } from 'dibs-react-intl';
import { BROWSE_TYPES, PAGE_TYPE } from 'dibs-constants/exports/pageTypes';
import { includes } from 'dibs-ts-utils/exports/includes';

import {
    type useQuickViewCategory_itemSearch$data,
    type useQuickViewCategory_itemSearch$key,
} from './__generated__/useQuickViewCategory_itemSearch.graphql';
import { type useQuickViewCategory_quickViewCategorySegments$key } from './__generated__/useQuickViewCategory_quickViewCategorySegments.graphql';

const itemSearchFragment = graphql`
    fragment useQuickViewCategory_itemSearch on ItemSearchQueryConnection {
        meta {
            header
        }
        pageType
        appliedFilters {
            name
            values {
                displayName
            }
        }
    }
`;

const paragraphAttributeNamesFragment = graphql`
    fragment useQuickViewCategory_quickViewCategorySegments on QuickViewCategorySegments {
        period
        origin
        style
        periodPrefix
    }
`;

const whitelistedPageTypes = BROWSE_TYPES.filter(type => type !== PAGE_TYPE.BUY);

const VALID_FILTER = {
    PERIOD_GROUP_FILTER: 'periodGroup',
    ESTATE_FILTER: 'estate',
    TOP_DESIGNER_FILTER: 'topDesigner',
} as const;

const VALID_FILTER_VALUES = Object.values(VALID_FILTER);

type ValidFilterValue = (typeof VALID_FILTER)[keyof typeof VALID_FILTER];

type AppliedFilter = NonNullable<
    NonNullable<useQuickViewCategory_itemSearch$data['appliedFilters']>[number]
>;

type BoolValueFilter = Exclude<ValidFilterValue, typeof VALID_FILTER.PERIOD_GROUP_FILTER>;

const periodGroupMessages = defineMessages({
    antique: {
        id: 'dibs-buyer-product-tile.QuickView.periodGroup.antique',
        defaultMessage: 'Antique',
    },
    retro: {
        id: 'dibs-buyer-product-tile.QuickView.periodGroup.retro',
        defaultMessage: 'Retro',
    },
    used: {
        id: 'dibs-buyer-product-tile.QuickView.periodGroup.used',
        defaultMessage: 'Used',
    },
    vintage: {
        id: 'dibs-buyer-product-tile.QuickView.periodGroup.vintage',
        defaultMessage: 'Vintage',
    },
});

type PeriodGroupValue = keyof typeof periodGroupMessages;

const appliedFilterMessages = defineMessages({
    estate: {
        id: 'dibs-buyer-product-tile.QuickView.estate',
        defaultMessage: 'Estate',
    },
    topDesigner: {
        id: 'dibs-buyer-product-tile.QuickView.topDesigner',
        defaultMessage: 'Designer',
    },
});

function getValidFilterRegExp({
    filterName,
    formatMessage,
}: {
    filterName: string;
    formatMessage?: IntlShape['formatMessage'];
}): RegExp {
    const messages =
        filterName === VALID_FILTER.PERIOD_GROUP_FILTER
            ? Object.values(periodGroupMessages)
            : Object.values(appliedFilterMessages);

    const formatFunction = formatMessage
        ? (message: MessageDescriptor) => formatMessage(message)
        : (message: MessageDescriptor) => message.defaultMessage;

    const filterValues = messages.map(formatFunction);

    return new RegExp(`\\b(${filterValues.join('|')})\\b`, 'i');
}

function swapPeriodGroup({
    text,
    periodGroup,
    periodGroupRegExp,
}: {
    text: string | null | undefined;
    periodGroup: string;
    periodGroupRegExp: RegExp;
}): string | null | undefined {
    return text?.replace(periodGroupRegExp, periodGroup);
}

function containsValidFilterValue(
    text: string | null | undefined,
    filterValueRegExp: RegExp
): boolean {
    return !!text && filterValueRegExp.test(text);
}

// Returns supported filter value for modifying buy page product image alt texts and quick view
function getValidFilterValue({
    validFilter,
    validFilterRegExp,
    formatMessage,
}: {
    validFilter: AppliedFilter | null;
    validFilterRegExp: RegExp;
    formatMessage: IntlShape['formatMessage'];
}): string | null {
    const filterName = validFilter?.name || '';
    let isValidFilter;
    let filterValue;

    if (!validFilter) {
        return null;
    }

    if (filterName === VALID_FILTER.PERIOD_GROUP_FILTER) {
        const periodGroup = validFilter?.values?.[0]?.displayName?.toLowerCase();
        isValidFilter = periodGroup && containsValidFilterValue(periodGroup, validFilterRegExp);
        filterValue = isValidFilter
            ? formatMessage(periodGroupMessages[periodGroup as PeriodGroupValue])
            : null;
    } else {
        isValidFilter =
            Object.keys(appliedFilterMessages).includes(filterName) &&
            validFilter?.values?.[0]?.displayName === 'True';
        filterValue = isValidFilter
            ? formatMessage(appliedFilterMessages[filterName as BoolValueFilter])
            : null;
    }

    return isValidFilter && filterValue ? filterValue : null;
}

export function useQuickViewCategory({
    itemSearch: itemSearchRef,
    paragraphAttributeNames: paragraphAttributeNamesRef,
}: {
    itemSearch: useQuickViewCategory_itemSearch$key | null | undefined;
    paragraphAttributeNames: useQuickViewCategory_quickViewCategorySegments$key | null | undefined;
}): string | null | undefined {
    const { formatMessage } = useIntl();
    const itemSearch = useFragment(itemSearchFragment, itemSearchRef);
    const paragraphAttributeNames = useFragment(
        paragraphAttributeNamesFragment,
        paragraphAttributeNamesRef
    );

    const { meta, pageType } = itemSearch || {};
    const pageHeading = meta?.header;

    if (paragraphAttributeNames && pageType && includes(whitelistedPageTypes, pageType)) {
        const filteredCategorySegments = Object.entries(paragraphAttributeNames)
            // GROWTH-5337: remove segment if it matches pageType
            .filter(([key]) => key !== pageType)
            .map(([, value]) => value);

        return [...filteredCategorySegments, pageHeading].join(' ');
    }

    const validFilter =
        itemSearch?.appliedFilters?.find(
            filter => filter?.name && VALID_FILTER_VALUES.includes(filter.name as ValidFilterValue)
        ) || null;
    const filterName = validFilter?.name || '';

    const translatedRegExp = getValidFilterRegExp({ filterName, formatMessage });
    const untranslatedRegExp = getValidFilterRegExp({ filterName });
    const pageHeadingContainsFilterValue = containsValidFilterValue(pageHeading, translatedRegExp);

    const validFilterValue = getValidFilterValue({
        validFilter,
        validFilterRegExp: untranslatedRegExp,
        formatMessage,
    });

    if (pageType === PAGE_TYPE.BUY && validFilterValue && paragraphAttributeNames) {
        const { period, origin, style } = paragraphAttributeNames;
        const paragraphSegments = [period, origin, style];
        let newPageHeading = pageHeading;

        if (filterName === VALID_FILTER.PERIOD_GROUP_FILTER && pageHeadingContainsFilterValue) {
            // https://1stdibs.atlassian.net/browse/GROWTH-6811?focusedCommentId=459423
            newPageHeading = swapPeriodGroup({
                text: pageHeading,
                periodGroup: validFilterValue,
                periodGroupRegExp: translatedRegExp,
            });
        } else if (!pageHeadingContainsFilterValue) {
            paragraphSegments.push(validFilterValue);
        }

        return [...paragraphSegments, newPageHeading].filter(Boolean).join(' ');
    } else {
        return null;
    }
}
